import earcut from "earcut";
import type { BooleanNode, SceneNode } from "@/document/model/nodes";
import { oklchToRgba, parseColor, type OklchColor } from "@/document/model/color";
import {
  gradientEndpointsForBounds,
  normalizeFillStack,
  normalizeImagePaint,
  normalizeGradientPaint,
  normalizeStrokeStack,
  radialGradientGeometryForBounds,
  sampleGradientPaint,
  type Paint,
} from "@/document/model/styles";
import { imageRectForPaint } from "@/document/operations/imagePaintGeometry";
import {
  boundsFromContours,
  contourParentsContour,
  contoursIntersect,
  contoursToPathData,
  normalizeNestedContourWindings,
  pathDataToContours,
  pointInPolygon,
  sceneNodeToContours,
  signedArea,
  transformContours,
  transformPathData,
  type Point,
} from "@/document/operations/pathGeometry";
import { resolveBooleanGeometryWithAvailablePathOps } from "@/document/operations/pathBooleanGeometry";
import type { Rect } from "@/lib/math/rect";
import { applyAffinePoint, type Affine2D } from "@/lib/math/affine";
import type { DisplayItem, DisplayPaint } from "../displayList";
import {
  fillTriangleChunkCacheByKey,
  formatCacheNumber,
  maxFillChunkCacheEntries,
  maxUnchangedFillChunkCacheEntries,
  pathMeshCache,
  setCappedCacheEntry,
  unchangedFillChunkCacheByKey,
  type CachedPathMesh,
} from "./cache";
import {
  contoursOrigin,
  getGeometryPool,
  sharedPathGeometryKey,
  translateContours,
  translatePathMesh,
} from "./geometryPool";
import { evenOddContourTriangles, flattenMeshes, packMesh, strokePolylineTriangles } from "./meshKernels";
import { strokeGeometryCacheKey } from "./strokeMesh";
export function booleanPreviewNode(node: BooleanNode, sourcePreviews: Map<string, Rect> | undefined): BooleanNode | undefined {
  if (!sourcePreviews || sourcePreviews.size === 0 || !node.sourceNodes.some(source => sourcePreviews.has(source.id))) {
    return undefined;
  }

  const cacheByNodeId = new Map((node.sourceGeometry ?? []).map(cache => [cache.nodeId, cache]));
  const operands = node.sourceNodes.flatMap(source => {
    if (!source.visible) {
      return [];
    }

    const previewBounds = sourcePreviews.get(source.id);
    const cached = cacheByNodeId.get(source.id);
    const freshContours = sceneNodeToContours(source);
    const baseContours = freshContours.length > 0
      ? freshContours
      : cached?.contours.map(contour => contour.map(point => ({ ...point }))) ?? [];
    if (baseContours.length === 0) {
      return [];
    }

    const fromBounds = freshContours.length > 0 ? source.bounds : cached?.sourceBounds ?? cached?.bounds ?? source.bounds;
    const toBounds = previewBounds ?? fromBounds;
    const contours = previewBounds ? transformContours(baseContours, fromBounds, toBounds) : baseContours;
    const path = cached?.path
      ? previewBounds
        ? transformPathData(cached.path, cached.sourceBounds ?? cached.bounds, toBounds)
        : cached.path
      : contoursToPathData(contours);
    return [{ nodeId: source.id, path, contours }];
  });
  if (operands.length === 0) {
    return {
      ...node,
      resolved: { path: "", contours: [] },
      bounds: boundsFromContours([]),
    };
  }

  const resolved = resolveBooleanGeometryWithAvailablePathOps(node.operation, operands);
  return {
    ...node,
    resolved: {
      path: resolved.path,
      contours: resolved.contours,
    },
    bounds: resolved.bounds,
  };
}

export function cachedPathMesh(node: SceneNode, key: string, contoursForKey: () => Point[][]): CachedPathMesh {
  const cached = pathMeshCache.get(node);
  if (cached && cached.key === key) {
    return cached;
  }

  const contours = contoursForKey();
  const origin = contoursOrigin(contours);
  const sharedKey = sharedPathGeometryKey(contours);
  const pool = getGeometryPool();
  const pooled = pool.getPathMesh(sharedKey);
  if (pooled) {
    const worldMesh = { ...translatePathMesh(pooled, origin), key };
    pathMeshCache.set(node, worldMesh);
    return worldMesh;
  }

  const localContours = translateContours(contours, -origin.x, -origin.y);
  const localMesh: CachedPathMesh = {
    key: sharedKey,
    contours: localContours,
    vertices: evenOddContourTriangles(localContours),
    strokeVerticesByWidth: new Map(),
  };
  pool.setPathMesh(sharedKey, localMesh);
  const worldMesh = { ...translatePathMesh(localMesh, origin), key };
  pathMeshCache.set(node, worldMesh);
  return worldMesh;
}

export function fillDisplayItems(
  node: SceneNode,
  kind: DisplayItem["kind"],
  bounds: Rect,
  vertices: Float32Array,
  clip: DisplayItem["clip"] | undefined,
  fallback: string,
  cornerRadii?: [number, number, number, number],
): DisplayItem[] {
  return normalizeFillStack(node.style).map(fill => ({
    id: node.id,
    kind,
    bounds,
    clip,
    cornerRadii,
    vertices,
    fill: paintToDisplayPaint(fill, node.bounds, fallback),
    scaleX: node.transform.scaleX,
    scaleY: node.transform.scaleY,
  }));
}

export function cachedStrokeDisplayItems(node: SceneNode, mesh: CachedPathMesh): DisplayItem[] {
  return normalizeStrokeStack(node.style).flatMap(stroke => {
    if (stroke.width <= 0) {
      return [];
    }

    const cacheKey = strokeGeometryCacheKey(stroke.width, stroke.align);
    let vertices = mesh.strokeVerticesByWidth.get(cacheKey);
    if (!vertices) {
      vertices = flattenMeshes(mesh.contours.map(contour => strokePolylineTriangles(contour, stroke.width, true, stroke.align)));
      mesh.strokeVerticesByWidth.set(cacheKey, vertices);
    }
    if (vertices.length === 0) {
      return [];
    }

    return [{
      id: node.id,
      kind: "triangles",
      bounds: boundsFromVertices(vertices),
      vertices,
      fill: paintToDisplayPaint(stroke.paint, node.bounds, "#ffffff"),
    }];
  });
}

export function normalizedCornerRadii(bounds: Rect, radii: [number, number, number, number]): [number, number, number, number] {
  const maxRadius = Math.min(Math.abs(bounds.width), Math.abs(bounds.height)) / 2;
  return radii.map(radius => Math.max(0, Math.min(maxRadius, radius))) as [number, number, number, number];
}

export function clipFromRoundedRect(
  bounds: Rect,
  worldTransform: Affine2D,
  cornerRadii: [number, number, number, number],
): NonNullable<DisplayItem["clip"]> {
  const polygon = roundedRectClipPolygon(bounds, cornerRadii).map(point => applyAffinePoint(worldTransform, point));
  return {
    bounds: boundsFromClipPoints(polygon),
    polygon,
  };
}

export function clipFromRect(bounds: Rect, worldTransform: Affine2D): NonNullable<DisplayItem["clip"]> {
  const polygon = rectPoints(bounds).map(point => applyAffinePoint(worldTransform, point));
  return {
    bounds: boundsFromClipPoints(polygon),
    polygon,
  };
}

export function roundedRectClipPolygon(
  bounds: Rect,
  cornerRadii: [number, number, number, number],
): Array<{ x: number; y: number }> {
  const width = Math.abs(bounds.width);
  const height = Math.abs(bounds.height);
  if (width <= 0 || height <= 0) {
    return rectPoints(bounds);
  }

  const left = Math.min(bounds.x, bounds.x + bounds.width);
  const right = Math.max(bounds.x, bounds.x + bounds.width);
  const top = Math.min(bounds.y, bounds.y + bounds.height);
  const bottom = Math.max(bounds.y, bounds.y + bounds.height);
  const [topLeft, topRight, bottomRight, bottomLeft] = cornerRadii;
  const stepsPerCorner = 8;

  const corners = [
    { center: { x: left + topLeft, y: top + topLeft }, radius: topLeft, start: Math.PI, end: Math.PI * 1.5 },
    { center: { x: right - topRight, y: top + topRight }, radius: topRight, start: Math.PI * 1.5, end: Math.PI * 2 },
    { center: { x: right - bottomRight, y: bottom - bottomRight }, radius: bottomRight, start: 0, end: Math.PI * 0.5 },
    { center: { x: left + bottomLeft, y: bottom - bottomLeft }, radius: bottomLeft, start: Math.PI * 0.5, end: Math.PI },
  ];
  const polygon: Array<{ x: number; y: number }> = [];
  for (const corner of corners) {
    if (corner.radius <= 0) {
      polygon.push({ x: corner.center.x, y: corner.center.y });
      continue;
    }
    for (let step = 0; step <= stepsPerCorner; step += 1) {
      const t = step / stepsPerCorner;
      const angle = corner.start + (corner.end - corner.start) * t;
      polygon.push({
        x: corner.center.x + Math.cos(angle) * corner.radius,
        y: corner.center.y + Math.sin(angle) * corner.radius,
      });
    }
  }
  return dedupeClipPoints(polygon, bounds);
}

function dedupeClipPoints(points: Array<{ x: number; y: number }>, bounds: Rect): Array<{ x: number; y: number }> {
  const deduped: Array<{ x: number; y: number }> = [];
  for (const point of points) {
    const previous = deduped.at(-1);
    if (!previous || previous.x !== point.x || previous.y !== point.y) {
      deduped.push(point);
    }
  }
  if (deduped.length > 1) {
    const first = deduped[0]!;
    const last = deduped[deduped.length - 1]!;
    if (first.x === last.x && first.y === last.y) {
      deduped.pop();
    }
  }
  return deduped.length >= 3 ? deduped : rectPoints(bounds);
}

export function intersectClips(left: DisplayItem["clip"] | undefined, right: NonNullable<DisplayItem["clip"]>): DisplayItem["clip"] {
  if (!left) {
    return right;
  }

  const polygon = intersectConvexPolygons(left.polygon, right.polygon);
  return {
    bounds: polygon.length >= 3 ? boundsFromClipPoints(polygon) : { x: 0, y: 0, width: 0, height: 0 },
    polygon,
  };
}

export function boundsFromClipPoints(points: Array<{ x: number; y: number }>): Rect {
  return points.length > 0 ? boundsFromPoints(points) : { x: 0, y: 0, width: 0, height: 0 };
}

function intersectConvexPolygons(
  subject: Array<{ x: number; y: number }>,
  clip: Array<{ x: number; y: number }>,
): Array<{ x: number; y: number }> {
  if (subject.length < 3 || clip.length < 3) {
    return [];
  }

  let output = subject;
  const winding = polygonSignedArea(clip) >= 0 ? 1 : -1;
  for (let index = 0; index < clip.length; index += 1) {
    const a = clip[index]!;
    const b = clip[(index + 1) % clip.length]!;
    const input = output;
    output = [];
    if (input.length === 0) {
      break;
    }

    let previous = input[input.length - 1]!;
    for (const current of input) {
      const currentInside = isInsideClipEdge(current, a, b, winding);
      const previousInside = isInsideClipEdge(previous, a, b, winding);
      if (currentInside) {
        if (!previousInside) {
          output.push(clipLineIntersection(previous, current, a, b));
        }
        output.push(current);
      } else if (previousInside) {
        output.push(clipLineIntersection(previous, current, a, b));
      }
      previous = current;
    }
  }

  return output.filter((point, index, points) => {
    const previous = points[(index + points.length - 1) % points.length];
    return !previous || Math.hypot(point.x - previous.x, point.y - previous.y) > 0.001;
  });
}

function isInsideClipEdge(
  point: { x: number; y: number },
  a: { x: number; y: number },
  b: { x: number; y: number },
  winding: number,
): boolean {
  const cross = (b.x - a.x) * (point.y - a.y) - (b.y - a.y) * (point.x - a.x);
  return winding * cross >= -0.001;
}

function clipLineIntersection(
  a: { x: number; y: number },
  b: { x: number; y: number },
  c: { x: number; y: number },
  d: { x: number; y: number },
): { x: number; y: number } {
  const abX = b.x - a.x;
  const abY = b.y - a.y;
  const cdX = d.x - c.x;
  const cdY = d.y - c.y;
  const denominator = abX * cdY - abY * cdX;
  if (Math.abs(denominator) < 1e-9) {
    return b;
  }

  const t = ((c.x - a.x) * cdY - (c.y - a.y) * cdX) / denominator;
  return {
    x: a.x + abX * t,
    y: a.y + abY * t,
  };
}

function polygonSignedArea(points: Array<{ x: number; y: number }>): number {
  let area = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index]!;
    const next = points[(index + 1) % points.length]!;
    area += current.x * next.y - next.x * current.y;
  }
  return area / 2;
}

export function rectPoints(bounds: { x: number; y: number; width: number; height: number }): Array<{ x: number; y: number }> {
  return [
    { x: bounds.x, y: bounds.y },
    { x: bounds.x + bounds.width, y: bounds.y },
    { x: bounds.x + bounds.width, y: bounds.y + bounds.height },
    { x: bounds.x, y: bounds.y + bounds.height },
  ];
}

/**
 * TS fallback even-odd triangulation (earcut). Prefer `evenOddContourTriangles`
 * from meshKernels — Wasm when enabled. Kept for nonzero compound helpers and
 * soft-fail registration.
 * @internal
 */
export function triangulateEvenOddContours(contours: Point[][]): number[] {
  const vertices: number[] = [];
  const normalizedContours = normalizeNestedContourWindings(contours);
  const rings = normalizedContours
    .map(removeDuplicatePoints)
    .filter(contour => contour.length >= 3)
    .map((contour, index) => ({
      contour,
      index,
      area: Math.abs(signedArea(contour)),
      depth: 0,
    }))
    .filter(ring => ring.area > 0.001)
    .sort((left, right) => right.area - left.area);

  for (const ring of rings) {
    ring.depth = rings.filter(candidate => candidate.index !== ring.index && candidate.area > ring.area && contourParentsContour(candidate.contour, ring.contour)).length;
  }

  const filledRings = rings.filter(ring => ring.depth % 2 === 0);
  for (const outer of filledRings) {
    const holes = rings.filter(ring => ring.depth % 2 === 1 && nearestFilledAncestor(ring, filledRings) === outer);
    appendNumbers(vertices, triangulatePolygonWithHoles(outer.contour, holes.map(hole => hole.contour)));
  }

  return vertices;
}

export function triangulateIndependentContours(contours: Point[][]): number[] {
  const vertices: number[] = [];
  for (const contour of contours) {
    appendNumbers(vertices, triangulateEvenOddContours([contour]));
  }
  return vertices;
}

function triangulateContourGroups(groups: Point[][][]): number[] {
  const vertices: number[] = [];
  for (const group of groups) {
    appendNumbers(vertices, triangulateEvenOddContours(group));
  }
  return vertices;
}

export function triangulateContourGroupChunks(groups: Point[][][], fillRule: "nonzero" | "evenodd"): Float32Array[] {
  return groups.flatMap(group => {
    const vertices = cachedContourGroupTriangles(group, fillRule);
    return vertices.length > 0 ? [vertices] : [];
  });
}

export function cachedContourGroupTriangles(group: Point[][], fillRule: "nonzero" | "evenodd"): Float32Array {
  const key = `${fillRule}:${contourGroupCacheKey(group)}`;
  const cached = fillTriangleChunkCacheByKey.get(key);
  if (cached) {
    return cached;
  }

  // Even-odd goes through meshKernels (Wasm when enabled). Nonzero compound stays TS.
  const vertices = fillRule === "nonzero"
    ? packMesh(triangulateNonzeroCompoundContours(group))
    : evenOddContourTriangles(group);
  setCappedCacheEntry(fillTriangleChunkCacheByKey, key, vertices, maxFillChunkCacheEntries);
  return vertices;
}

function triangulateNonzeroCompoundContours(contours: Point[][]): number[] {
  if (contours.length <= 1) {
    return triangulateEvenOddContours(contours);
  }

  const hasIntersectingPair = contours.some((left, leftIndex) =>
    contours.some((right, rightIndex) =>
      leftIndex !== rightIndex && contoursIntersect(left, right)));

  if (hasIntersectingPair) {
    const vertices: number[] = [];
    for (const contour of contours) {
      appendNumbers(vertices, triangulateEvenOddContours([contour]));
    }
    return vertices;
  }

  return triangulateEvenOddContours(contours);
}

export function cachedUnchangedFillChunk(mesh: CachedPathMesh, changedEdgeIds: Set<string>, chunks: Float32Array[]): Float32Array {
  if (chunks.length === 0) {
    return new Float32Array(0);
  }

  const key = `${mesh.key}:unchanged:${[...changedEdgeIds].sort().join(",")}`;
  const cached = unchangedFillChunkCacheByKey.get(key);
  if (cached) {
    return cached;
  }

  const vertices = flattenMeshes(chunks);
  setCappedCacheEntry(unchangedFillChunkCacheByKey, key, vertices, maxUnchangedFillChunkCacheEntries);
  return vertices;
}

function contourGroupCacheKey(group: Point[][]): string {
  return group.map(contour => contour.map(point => `${formatCacheNumber(point.x)},${formatCacheNumber(point.y)}`).join(";")).join("/");
}

export function flattenNumberChunks(chunks: number[][]): number[] {
  const vertices: number[] = [];
  for (const chunk of chunks) {
    appendNumbers(vertices, chunk);
  }
  return vertices;
}

export function appendNumbers(target: number[], values: number[]): void {
  for (const value of values) {
    target.push(value);
  }
}

function nearestFilledAncestor(ring: { contour: Point[]; area: number }, filledRings: Array<{ contour: Point[]; area: number }>): { contour: Point[]; area: number } | undefined {
  let nearest: { contour: Point[]; area: number } | undefined;
  for (const candidate of filledRings) {
    if (candidate.area <= ring.area || !contourParentsContour(candidate.contour, ring.contour)) {
      continue;
    }

    if (!nearest || candidate.area < nearest.area) {
      nearest = candidate;
    }
  }

  return nearest;
}

function triangulatePolygonWithHoles(outer: Point[], holes: Point[][]): number[] {
  const normalized = normalizeNestedContourWindings([outer, ...holes]);
  const normalizedOuter = normalized[0]!;
  const normalizedHoles = normalized.slice(1);
  const data: number[] = [];
  const holeIndices: number[] = [];
  const rings = [normalizedOuter, ...normalizedHoles];

  for (const ring of rings) {
    if (ring !== normalizedOuter) {
      holeIndices.push(data.length / 2);
    }

    for (const point of ring) {
      data.push(point.x, point.y);
    }
  }

  const indices = earcut(data, holeIndices, 2);
  return indices.flatMap(index => [data[index * 2]!, data[index * 2 + 1]!]);
}

function interiorPoint(points: Point[]): Point {
  const centroid = polygonCentroid(points);
  if (pointInPolygon(centroid, points)) {
    return centroid;
  }

  for (let index = 0; index < points.length; index += 1) {
    const a = points[index]!;
    const b = points[(index + 1) % points.length]!;
    const midpoint = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    const candidate = { x: (midpoint.x + centroid.x) / 2, y: (midpoint.y + centroid.y) / 2 };
    if (pointInPolygon(candidate, points)) {
      return candidate;
    }
  }

  return points[0] ?? centroid;
}

function polygonCentroid(points: Point[]): Point {
  let area = 0;
  let x = 0;
  let y = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index]!;
    const next = points[(index + 1) % points.length]!;
    const factor = current.x * next.y - next.x * current.y;
    area += factor;
    x += (current.x + next.x) * factor;
    y += (current.y + next.y) * factor;
  }

  if (Math.abs(area) <= Number.EPSILON) {
    return {
      x: points.reduce((sum, point) => sum + point.x, 0) / Math.max(1, points.length),
      y: points.reduce((sum, point) => sum + point.y, 0) / Math.max(1, points.length),
    };
  }

  return { x: x / (3 * area), y: y / (3 * area) };
}

export function removeDuplicatePoints(points: Array<{ x: number; y: number }>): Array<{ x: number; y: number }> {
  const deduped: Array<{ x: number; y: number }> = [];
  for (const point of points) {
    const previous = deduped.at(-1);
    if (!previous || Math.hypot(previous.x - point.x, previous.y - point.y) > 0.001) {
      deduped.push(point);
    }
  }

  const first = deduped[0];
  const last = deduped.at(-1);
  if (first && last && Math.hypot(first.x - last.x, first.y - last.y) <= 0.001) {
    deduped.pop();
  }

  return deduped;
}

export function rectVertices(rect: { x: number; y: number; width: number; height: number }): number[] {
  return [
    rect.x,
    rect.y,
    rect.x + rect.width,
    rect.y,
    rect.x,
    rect.y + rect.height,
    rect.x,
    rect.y + rect.height,
    rect.x + rect.width,
    rect.y,
    rect.x + rect.width,
    rect.y + rect.height,
  ];
}

export function boundsFromPoints(points: Array<{ x: number; y: number }>): { x: number; y: number; width: number; height: number } {
  if (points.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  let left = points[0]!.x;
  let top = points[0]!.y;
  let right = left;
  let bottom = top;
  for (let index = 1; index < points.length; index += 1) {
    const point = points[index]!;
    left = Math.min(left, point.x);
    top = Math.min(top, point.y);
    right = Math.max(right, point.x);
    bottom = Math.max(bottom, point.y);
  }
  return { x: left, y: top, width: right - left, height: bottom - top };
}

export function boundsFromVertices(vertices: ArrayLike<number>): { x: number; y: number; width: number; height: number } {
  if (vertices.length < 2) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  let left = vertices[0]!;
  let top = vertices[1]!;
  let right = left;
  let bottom = top;
  for (let index = 2; index < vertices.length; index += 2) {
    const x = vertices[index]!;
    const y = vertices[index + 1]!;
    left = Math.min(left, x);
    top = Math.min(top, y);
    right = Math.max(right, x);
    bottom = Math.max(bottom, y);
  }
  return { x: left, y: top, width: right - left, height: bottom - top };
}

export function paintToDisplayPaint(paint: Paint | undefined, bounds: { x: number; y: number; width: number; height: number }, fallback: string | OklchColor): DisplayPaint {
  if (paint?.type === "image") {
    const normalized = normalizeImagePaint(paint);
    return {
      type: "image",
      assetId: normalized.assetId,
      source: normalized.source,
      imageRect: imageRectForPaint(bounds, normalized),
      flipX: normalized.flipX,
      flipY: normalized.flipY,
      opacity: normalized.opacity,
      adjustments: normalized.adjustments,
    };
  }

  if (paint?.type === "linearGradient") {
    const normalized = normalizeGradientPaint(paint);
    const vector = gradientEndpointsForBounds(bounds, normalized);
    return {
      type: "linearGradient",
      start: [vector.start.x, vector.start.y],
      end: [vector.end.x, vector.end.y],
      stops: sampleGradientPaint(normalized, 32).slice(0, 32).map(stop => ({
        position: stop.position,
        color: colorToRgba(stop.color),
      })),
    };
  }

  if (paint?.type === "radialGradient") {
    const normalized = normalizeGradientPaint(paint);
    const geometry = radialGradientGeometryForBounds(bounds, normalized);
    return {
      type: "radialGradient",
      center: [geometry.center.x, geometry.center.y],
      radius: geometry.radius,
      stops: sampleGradientPaint(normalized, 32).slice(0, 32).map(stop => ({
        position: stop.position,
        color: colorToRgba(stop.color),
      })),
    };
  }

  return {
    type: "solid",
    color: colorToRgba(paint?.type === "solid" ? paint.color : fallback),
  };
}

function colorToRgba(color: string | OklchColor): [number, number, number, number] {
  return oklchToRgba(parseColor(color));
}
